// MACHINERY V2 (Stroje) — dialog host for the fleet surface (M2.2).
//
// >>> THE ONE SANCTIONED MUI IMPORT SITE IN v2. <<<
//
// Ticket §5: "The kebab actions (QR / Vyradiť) are listed in the designer but
// their dialogs are NOT redesigned — v2 reuses the existing dialogs (they open
// over the v2 surface) until separately scheduled." Those dialogs are MUI
// components, so v2 has to mount them somewhere. Confining every one of them to
// THIS file means the v2 stack rule ("no MUI in the v2 chrome") still holds
// everywhere a v2 SURFACE is drawn, and the day each dialog is redesigned its
// import disappears from exactly one place.
//
// Nothing here is styled, wrapped or re-skinned — the dialogs render exactly as
// they do on the live grid. This is a mount point, not a component.
//
// DIALOG STATE LIVES IN THE PAGE (FleetV1Page), not here: the same dialog is
// opened from a card kebab and from the toolbar, so a single owner keeps the
// open/close pairs honest.
import React from "react";

import { CatalogModelListItem, Machine } from "../../models/models";
import AssignGroupDialog from "../../components/AssignGroupDialog";
import DecommissionDialog from "../../components/DecommissionDialog";
import MachineEditDialog from "../../components/MachineEditDialog";
import MachineQRCodeDialog from "../../components/MachineQRCodeDialog";
import ManageGroupsDialog from "../../components/ManageGroupsDialog";
import CatalogAddDialogV2 from "../catalog/CatalogAddDialogV2";

export interface FleetDialogsProps {
  /** The slice-selected machine. Since M2.8 removed the MachineDialog mount,
   *  the QR dialog is its only remaining reader. */
  selectedMachine?: Machine;
  /**
   * M2.8 CLOSED THIS OUT. M2.7 repointed the toolbar "+" to the v2
   * `add/AddMachinePicker` but left ONE path on v1's MUI `MachineDialog`: a
   * catalog model picked in the picker opened it in `catalog-search` mode via
   * `preSelectedCatalogModel` (MachineDialog.tsx:487-492), the reduced serial +
   * year + location form. That form now exists as `catalog/CatalogAddDialogV2`,
   * so the mount is swapped and the `MachineDialog` import is GONE from this
   * file — the "the day each dialog is redesigned its import disappears from
   * exactly one place" story above, playing out.
   * The dialog renders only when a model was actually picked: without one there
   * is nothing to add FROM, which is the v2 picker's job, not this mount's.
   * NOTE: "Upraviť detaily" does NOT come through here — the edit path is
   * `MachineEditDialog`, mounted separately below and still MUI by design.
   */
  addOpen: boolean;
  preSelectedCatalogModel?: CatalogModelListItem;
  onAddClose: () => void;
  /** "Späť na možnosti" / "Zmeniť stroj" (artboard 0903). REQUIRED here: this
   *  mount is reached FROM the picker, so it always has somewhere to go back
   *  to, and `CatalogAddDialogV2` draws both controls only when it is given.
   *  See useFleetDialogs.ts for why the picker's flag is the page's. */
  onAddBack: () => void;
  onCreated: (id?: string) => void;
  qrOpen: boolean;
  onQrClose: () => void;
  manageGroupsOpen: boolean;
  onManageGroupsClose: () => void;
  assignGroupOpen: boolean;
  assignGroupMachine: Machine | null;
  onAssignGroupClose: () => void;
  onAssigned: () => void;
  editMachine: Machine | null;
  onEditClose: () => void;
  onEditSaved: () => void;
  decommissionMachine: Machine | null;
  onDecommissionClose: () => void;
  onDecommissioned: () => void;
}

const FleetDialogs: React.FC<FleetDialogsProps> = ({
  selectedMachine, addOpen, preSelectedCatalogModel, onAddClose, onAddBack, onCreated,
  qrOpen, onQrClose,
  manageGroupsOpen, onManageGroupsClose, assignGroupOpen, assignGroupMachine,
  onAssignGroupClose, onAssigned, editMachine, onEditClose, onEditSaved,
  decommissionMachine, onDecommissionClose, onDecommissioned,
}) => (
  <>
    {addOpen && !!preSelectedCatalogModel && (
      <CatalogAddDialogV2
        open
        model={preSelectedCatalogModel}
        onClose={onAddClose}
        onBack={onAddBack}
        onCreated={onCreated}
      />
    )}

    {!!selectedMachine && (
      <MachineQRCodeDialog
        key={`qr-code-dialog-${qrOpen}`}
        open={qrOpen}
        onClose={onQrClose}
        machine={selectedMachine}
      />
    )}

    <ManageGroupsDialog open={manageGroupsOpen} onClose={onManageGroupsClose} />

    <AssignGroupDialog
      open={assignGroupOpen}
      onClose={onAssignGroupClose}
      machine={assignGroupMachine}
      onAssigned={onAssigned}
    />

    {!!editMachine && (
      <MachineEditDialog
        open
        machine={editMachine}
        onClose={onEditClose}
        onSaved={onEditSaved}
      />
    )}

    <DecommissionDialog
      open={!!decommissionMachine}
      machine={decommissionMachine}
      onClose={onDecommissionClose}
      onDecommissioned={onDecommissioned}
    />
  </>
);

export default FleetDialogs;
